/**
 * Vector annotation model for the CoPaya screenshot editor.
 *
 * Annotations are kept as data (ops) and replayed onto a canvas instead of
 * painting destructively — that is what makes undo/clear a pure state change
 * and lets the in-progress drag render as a draft without committing it.
 *
 * All coordinates live in the captured image's own pixel space (device px),
 * so exporting is a plain replay and the on-screen mapping is a single scale
 * factor owned by the editor component.
 */

export type AnnotationTool = 'pen' | 'rect' | 'arrow' | 'text';

export interface Point {
  x: number;
  y: number;
}

export type AnnotationOp =
  | { tool: 'pen'; color: string; strokeWidth: number; points: Point[] }
  | {
      tool: 'rect';
      color: string;
      strokeWidth: number;
      x: number;
      y: number;
      width: number;
      height: number;
    }
  | { tool: 'arrow'; color: string; strokeWidth: number; from: Point; to: Point }
  | { tool: 'text'; color: string; fontSize: number; x: number; y: number; text: string };

export type AnnotationAction =
  | { type: 'add'; op: AnnotationOp }
  | { type: 'undo' }
  | { type: 'clear' };

export function annotationsReducer(
  ops: readonly AnnotationOp[],
  action: AnnotationAction,
): readonly AnnotationOp[] {
  switch (action.type) {
    case 'add':
      return [...ops, action.op];
    case 'undo':
      return ops.length === 0 ? ops : ops.slice(0, -1);
    case 'clear':
      return ops.length === 0 ? ops : [];
  }
}

/**
 * The two wing points of an arrowhead whose tip sits at `to`, swept back
 * toward `from`. Pure geometry, unit-testable without a canvas.
 */
export function arrowHeadPoints(from: Point, to: Point, size: number): [Point, Point] {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const wingAngle = Math.PI / 7;
  return [
    {
      x: to.x - size * Math.cos(angle - wingAngle),
      y: to.y - size * Math.sin(angle - wingAngle),
    },
    {
      x: to.x - size * Math.cos(angle + wingAngle),
      y: to.y - size * Math.sin(angle + wingAngle),
    },
  ];
}

/** Multiplier from stroke width to arrowhead size. */
const ARROW_HEAD_SCALE = 4;

/** Replay ops onto a 2D context. `fontFamily` comes from the MUI theme. */
export function drawOps(
  ctx: CanvasRenderingContext2D,
  ops: readonly AnnotationOp[],
  fontFamily: string,
): void {
  for (const op of ops) {
    ctx.save();
    switch (op.tool) {
      case 'pen': {
        if (op.points.length < 2) break;
        ctx.strokeStyle = op.color;
        ctx.lineWidth = op.strokeWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        const [first, ...rest] = op.points;
        if (!first) break;
        ctx.beginPath();
        ctx.moveTo(first.x, first.y);
        for (const p of rest) ctx.lineTo(p.x, p.y);
        ctx.stroke();
        break;
      }
      case 'rect': {
        ctx.strokeStyle = op.color;
        ctx.lineWidth = op.strokeWidth;
        ctx.lineJoin = 'round';
        // Normalize so a drag toward the top-left still strokes a valid rect.
        const x = Math.min(op.x, op.x + op.width);
        const y = Math.min(op.y, op.y + op.height);
        ctx.strokeRect(x, y, Math.abs(op.width), Math.abs(op.height));
        break;
      }
      case 'arrow': {
        ctx.strokeStyle = op.color;
        ctx.fillStyle = op.color;
        ctx.lineWidth = op.strokeWidth;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(op.from.x, op.from.y);
        ctx.lineTo(op.to.x, op.to.y);
        ctx.stroke();
        const [left, right] = arrowHeadPoints(op.from, op.to, op.strokeWidth * ARROW_HEAD_SCALE);
        ctx.beginPath();
        ctx.moveTo(op.to.x, op.to.y);
        ctx.lineTo(left.x, left.y);
        ctx.lineTo(right.x, right.y);
        ctx.closePath();
        ctx.fill();
        break;
      }
      case 'text': {
        ctx.fillStyle = op.color;
        ctx.font = `${op.fontSize}px ${fontFamily}`;
        ctx.textBaseline = 'top';
        ctx.fillText(op.text, op.x, op.y);
        break;
      }
    }
    ctx.restore();
  }
}

/**
 * Flatten the screenshot and its annotations into a PNG.
 *
 * Drawing is synchronous — only the PNG encoding is promised — so calling
 * this inside a click handler keeps the user gesture intact for the
 * clipboard write that consumes the result.
 */
export function exportAnnotatedPng(
  base: HTMLCanvasElement,
  ops: readonly AnnotationOp[],
  fontFamily: string,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = base.width;
  canvas.height = base.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return Promise.reject(new Error('Canvas 2D context unavailable'));
  }
  ctx.drawImage(base, 0, 0);
  drawOps(ctx, ops, fontFamily);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('PNG encoding failed'));
    }, 'image/png');
  });
}

/** Maximum length the entry-link API accepts for `prefillSummary`. */
export const SUMMARY_MAX_LENGTH = 500;

/**
 * Match the server's zod normalization (`trim().max(500)`) so what the user
 * sees accepted locally is exactly what the API accepts.
 */
export function clampSummary(summary: string): string {
  return summary.trim().slice(0, SUMMARY_MAX_LENGTH);
}
